import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Snackbar, Alert, Box, CircularProgress, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';
import '@fontsource/poppins/300.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/700.css';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NudgeProvider } from './features/nudges/NudgeProvider';
import { BASE_URL } from './config/Config';
import PWAInstallPrompt from './components/common/PWAInstallPrompt';

// Pages
import Login from './pages/Login';
import ProfileBuilder from './pages/ProfileBuilder';
import QuestionBank from './pages/teacher/QuestionBank';
import TestsPage from './pages/teacher/TestsPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import ManageUsersPage from './pages/admin/ManageUsersPage';
import SubjectTopicPage from './pages/admin/SubjectTopicPage';
import SubjectsPage from './pages/admin/SubjectsPage';
import SupportRequestsPage from './pages/admin/SupportRequestsPage';
import FeatureManagementPage from './pages/admin/FeatureManagementPage';
import PricingPlanManagementPage from './pages/admin/PricingPlanManagementPage';
import FeatureAccessManagementPage from './pages/admin/FeatureAccessManagementPage';
import TenantManagementPage from './pages/admin/TenantManagementPage';
import BoardManagementPage from './pages/admin/BoardManagementPage';
import SemesterTemplatesPage from './pages/admin/SemesterTemplatesPage';
import AcademicYearManagementPage from './pages/admin/AcademicYearManagementPage';
import DifficultyManagementPage from './pages/admin/DifficultyManagementPage';
import UserSubscriptionManagementPage from './pages/admin/UserSubscriptionManagementPage.jsx';
import AddOnCatalogManagementPage from './pages/admin/AddOnCatalogManagementPage.jsx';
import ThrottleConfigPage from './pages/admin/ThrottleConfigPage';
import AIPromptsPage from './pages/admin/AIPromptsPage';
import MetaTagManagementPage from './pages/admin/MetaTagManagementPage';
import ExamModeManagementPage from './pages/admin/ExamModeManagementPage.jsx';
import StatusManagementPage from './pages/admin/StatusManagementPage';
import AdminQuestionBankPage from './pages/admin/AdminQuestionBankPage';
import AdminQuestionAnalyticsPage from './pages/admin/AdminQuestionAnalyticsPage';
import AdminReferralPage from './pages/referral/AdminReferralPage.js';
import InviteLanding from './components/referral/InviteLanding';
import StudentAssignmentsPage from './pages/teacher/StudentAssignmentsPage.jsx';
import Dashboard from './pages/Dashboard';
import TeacherDashboard from './pages/teacher/TeacherDashboard';
import TeacherLayout from './components/layout/TeacherLayout';
import ParentLayout from './components/layout/ParentLayout';
import ParentDashboard from './pages/parent/ParentDashboard';
import ParentChildrenPage from './pages/parent/ChildrenPage';
import StudentsPage from './pages/teacher/StudentsPage';
import TeacherBatchesPage from './pages/teacher/TeacherBatchesPage';
import TeacherBatchDetailPage from './pages/teacher/TeacherBatchDetailPage';
import Profile from './pages/Profile';
import Tests from './pages/tests/Tests';
import TestTaking from './pages/tests/TestTaking';
import TestInstructions from './pages/tests/Instructions';
import TestResultsPage from './pages/tests/ResultsPage';
import ReviewAnswers from './pages/tests/ReviewAnswers.js';
import ResultsPage from './pages/student/ResultsPage';
import ChangePassword from './pages/settings/ChangePassword';
import Register from './pages/Register';
import InviteRegister from './pages/InviteRegister';
import LandingPage from './pages/LandingPage';
import AwardsSetup from './pages/teacher/AwardsSetup';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import RevisionNotesPage from './pages/student/RevisionNotesPage';
import ExamPrepPlannerPage from './pages/student/ExamPrepPlannerPage';
import VerifyEmail from './pages/VeryifyEmail.js';
import VerifyParentConsent from './pages/VerifyParentConsent';
import StudentCompleteProfile from './pages/StudentCompleteProfile';
import TeacherCompleteProfile from './pages/TeacherCompleteProfile';
import AcceptInvitePage from './pages/AcceptInvitePage';
import MobileInviteLinkPage from './pages/MobileInviteLinkPage';
import Pricing from './pages/Pricing';
import SupportRequestPage from './pages/SupportRequestPage.jsx';
import LegalContentPage from './pages/LegalContentPage';
import PaymentPage from './pages/PaymentPage';
import PaymentFailedPage from './pages/PaymentFailedPage';
import AddOnPaymentPage from './pages/AddOnPaymentPage';
import GoogleCalendarCallback from './pages/GoogleCalendarCallback';
import Calendar from './pages/teacher/Calendar';
import TeacherDashboardAnalytics from './pages/analytics/TeacherDashboardAnalytics';
import TeacherAnalyticsWrapper from './pages/analytics/TeacherAnalyticsWrapper';
import StudentAnalyticsDashboard from './pages/analytics/StudentAnalyticsDashboard';
import AnalyticsIndex from './pages/analytics';

// Champions pages
import ChampionsDashboard from './pages/champions/ChampionsDashboard';
import ChampionsReferralWrapper from './pages/champions/ChampionsReferralWrapper';
import ChampionsQuestionBankWrapper from './pages/champions/ChampionsQuestionBankWrapper';
// Theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#3f51b5',  // Bright blue
      light: '#757de8',
      dark: '#002984',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f50057',  // Pink
      light: '#ff4081',
      dark: '#c51162',
      contrastText: '#ffffff',
    },
    success: {
      main: '#4caf50',  // Green
      light: '#81c784',
      dark: '#388e3c',
    },
    warning: {
      main: '#ff9800',  // Orange
      light: '#ffb74d',
      dark: '#f57c00',
    },
    error: {
      main: '#f44336',  // Red
      light: '#e57373',
      dark: '#d32f2f',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 500,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 500,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 500,
      fontSize: '1.5rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
      borderRadius: 8,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
          },
        },
        contained: {
          '&:hover': {
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
      },
    },
  },
});

// Protected route component with role-based access
const ProtectedRoute = ({ children, roles = [] }) => {
  
  
  const [status, setStatus] = React.useState('checking');
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const storedToken = localStorage.getItem('token') || sessionStorage.getItem('token');
  
  const location = useLocation();
  const isMounted = React.useRef(true);
  const authCheckInProgress = React.useRef(false);
  const lastAuthCheck = React.useRef(0);
  const initialRender = React.useRef(true);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  React.useEffect(() => {
    // Skip if auth check is already in progress
    if (authCheckInProgress.current) {
      
      return;
    }

    // Early redirect if no stored token
    const storedToken = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!storedToken) {
      setStatus('unauthenticated');
      if (location.pathname !== '/login') {
        navigate('/login', { replace: true });
      }
      authCheckInProgress.current = false;
      return;
    }

    // On initial render, always perform the check
    if (initialRender.current) {
      initialRender.current = false;
    } 
    // Otherwise, skip if we've checked recently (within 1000ms)
    else {
      const currentTime = Date.now();
      if (currentTime - lastAuthCheck.current < 1000) {
        console.log('[ProtectedRoute] Auth checked too recently, skipping');
        return;
      }
    }
    
    lastAuthCheck.current = Date.now();
    authCheckInProgress.current = true;
    
    const checkAuth = async () => {
      try {
        console.log('[ProtectedRoute] Starting auth check for path:', location.pathname);
        
        // Check if user is authenticated (synchronous check first)
        const isAuth = isAuthenticated();
        
        // If component was unmounted during async operation
        if (!isMounted.current) {
          console.log('[ProtectedRoute] Component unmounted during auth check');
          authCheckInProgress.current = false;
          return;
        }
        
        console.log('[ProtectedRoute] Auth check result:', { 
          isAuth, 
          hasUser: !!user, 
          path: location.pathname,
          roles: roles.join(',')
        });

        if (!isAuth) {
          setStatus('unauthenticated');
          console.log('[ProtectedRoute] Not authenticated, redirecting to login');
          // Reset lastAuthCheck so that auth checks run immediately on login page
          lastAuthCheck.current = 0;
          // Only navigate if we're not already on the login page to prevent loops
          if (location.pathname !== '/login') {
            navigate('/login', { 
              replace: true, 
              state: { 
                from: location.pathname,
                timestamp: Date.now()
              } 
            });
            // Fallback: if after a short delay we are still on a protected path, force a full redirect
            setTimeout(() => {
              if (window.location.pathname !== '/login') {
                window.location.href = '/login';
              }
            }, 100);
          }
          return;
        }

        // If we have required roles, check them
        if (roles.length > 0 && user) {
          const hasRole = roles.some(role => {
            const normalizedRole = role.toLowerCase().trim();
            const userRole = (user.role_name || '').toLowerCase().trim();
            
            // Check role directly or via isXxx flag
            const roleCheck = userRole === normalizedRole;
            const flagName = `is${role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()}`;
            const flagCheck = user[flagName] === true || user[`is_${normalizedRole}`] === true;
            
            console.log(`[ProtectedRoute] Role check for ${role}:`, { 
              roleCheck, 
              flagCheck, 
              userRole, 
              normalizedRole, 
              flagName,
              user: { ...user, password: undefined } // Don't log password
            });
            
            return roleCheck || flagCheck;
          });

          if (!hasRole) {
            setStatus('unauthenticated');
            console.log('[ProtectedRoute] Unauthorized, missing required role');
            // Only navigate if we're not already on the unauthorized page
            if (location.pathname !== '/unauthorized') {
              navigate('/unauthorized', { 
                replace: true,
                state: { 
                  from: location.pathname,
                  timestamp: Date.now()
                }
              });
            }
            return;
          }
        }
        
        // If we get here, user is authenticated and authorized
        // Student safeguard: require profile basics and grade selection before full access
        const isStudent = user.isStudent || (user.role_name && user.role_name.toString().toLowerCase() === 'student');
        const needsCompleteProfile = !!(user.requires_profile_completion || (isStudent && user.requires_grade_selection));
        const completeProfileAllowedPaths = ['/complete-profile', '/student/complete-profile', '/']; // allow site home even if profile incomplete
        if (needsCompleteProfile && !completeProfileAllowedPaths.includes(location.pathname)) {
          setStatus('authenticated');
          navigate('/complete-profile', { replace: true });
          authCheckInProgress.current = false;
          return;
        }

        const parentConsentRestricted =
          !!(isStudent && user.requires_parent_consent);
        const parentConsentAllowedPaths = ['/dashboard', '/profile', '/settings/change-password', '/support'];
        if (parentConsentRestricted && !parentConsentAllowedPaths.includes(location.pathname)) {
          setStatus('authenticated');
          navigate('/dashboard', { replace: true });
          authCheckInProgress.current = false;
          return;
        }

        const isParent = user.isParent || user.is_parent || (user.role_name && user.role_name.toString().toLowerCase() === 'parent');
        const isTeacher = user.isTeacher || user.is_teacher || (user.role_name && user.role_name.toString().toLowerCase() === 'teacher');
        let profileComplete = true;

        console.log('[ProtectedRoute] User authenticated, authorized, and profile complete');
        setStatus('authenticated');

      } catch (error) {
        console.error('[ProtectedRoute] Error during authentication check:', error);
        if (isMounted.current) {
          setStatus('unauthenticated');
        }
        // Only navigate if we're not already on the login page
        if (location.pathname !== '/login') {
          navigate('/login', { 
            replace: true, 
            state: { 
              from: location.pathname,
              error: 'An error occurred during authentication',
              timestamp: Date.now()
            } 
          });
        }
      } finally {
        if (isMounted.current) {
          authCheckInProgress.current = false;
            if (status === 'checking') {
              setStatus(user ? 'authenticated' : 'unauthenticated');
            }
        }
      }
    };
    
    checkAuth();
  }, [navigate, user, isAuthenticated, roles, location]);

  // Check for recent logout state first
  const timeSinceLogout = Date.now() - (window.lastLogoutAt || 0);
  const isRecentLogout = timeSinceLogout < 3000;
  
  // If we're in a recent logout state, redirect immediately
  if (isRecentLogout) {
    console.log('[ProtectedRoute] Recent logout detected, redirecting immediately');
    return <Navigate to="/login" replace />;
  }

  // If no token at render time, redirect immediately to login
  if (!storedToken) {
    console.log('[ProtectedRoute] No token found, redirecting immediately');
    return <Navigate to="/login" replace />;
  }

  // Show loading state only if we're still checking auth and not already authenticated
  if (status === 'checking' && !user) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="body1" color="textSecondary">
          Verifying your credentials...
        </Typography>
      </Box>
    );
  }
  
  // If we get here, user is authenticated and authorized
  return children;
};

function TestFlowShell({ children }) {
  const { user } = useAuth();
  if (!user) return children;
  if (user.isTeacher || user.is_teacher) {
    return <TeacherLayout>{children}</TeacherLayout>;
  }
  if (user.isParent || user.is_parent) {
    return <ParentLayout>{children}</ParentLayout>;
  }
  return children;
}

function App() {
  const [error, setError] = React.useState(null);
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  
  // Global session conflict state
  const [showSessionDialog, setShowSessionDialog] = React.useState(false);
  const [sessionDialogData, setSessionDialogData] = React.useState(null);
  
  const { user, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Debug auth state changes (commented out to prevent infinite loops)
  // React.useEffect(() => {
  //   console.log('Auth state changed:', { 
  //     isAuthenticated: isAuthenticated(),
  //     loading,
  //     user: user ? { id: user.id, email: user.email } : null,
  //     currentPath: location.pathname
  //   });
  // }, [loading, user, location.pathname]);

  // Handle errors from auth context
  React.useEffect(() => {
    if (error) {
      console.error('Auth error:', error);
      setError(error);
      setOpenSnackbar(true);
    }
  }, [error]);

  // Track if a redirection is in progress to prevent loops
  const isRedirecting = React.useRef(false);
  const authCheckInProgress = React.useRef(false);
  const previousPath = React.useRef('');
  const lastAuthCheck = React.useRef(0);

  // Redirect based on authentication status
  React.useEffect(() => {
    let isMounted = true;
    
    const checkAuthAndRedirect = async () => {

      // this was added to avoid redirecting public pages to /login
      const publicPaths = [
        '/',
        '/invite',
        '/join',
        '/support',
        '/get-started',
        '/login',
        '/register',
        '/forgot-password',
        '/reset-password',
        '/app-link',
        '/accept-invite',
        '/verify-email',
        '/invite-register',
        '/payment',
        '/payment-failed',
        '/terms',
        '/privacy',
        '/verify-parent-consent',
      ];
      const isPublicRoute = publicPaths.some(path => location.pathname === path || location.pathname.startsWith(path + '/'));
     
           // Don't run redirect logic if already in progress or unmounted
      if (authCheckInProgress.current || !isMounted) {
        console.log('[Auth] Auth check already in progress or unmounted, skipping');
        return;
      }

      // Don't run redirect logic until auth state is fully initialized
      if (loading) {
        console.log('[Auth] Still loading, skipping redirection');
        return;
      }
      
      const currentPath = location.pathname;
      const isAuthPage = ['/','/login', '/register', '/forgot-password', '/reset-password', '/verify-email'].includes(currentPath);
      
      // Skip if we've already processed this path or checked recently
      const currentTime = Date.now();
      if (previousPath.current === currentPath || (currentTime - lastAuthCheck.current < 500)) {
        console.log('[Auth] Path unchanged or checked recently, skipping check');
        return;
      }
      
      previousPath.current = currentPath;
      lastAuthCheck.current = Date.now();
      authCheckInProgress.current = true;
      isRedirecting.current = false;
      
      try {
        console.log('[Auth] Starting auth check for path:', currentPath);
        
        // Check authentication synchronously
        const isAuth = isAuthenticated();
        
        if (!isMounted) {
          console.log('[Auth] Component unmounted, aborting');
          return;
        }
        
        console.log('[Auth] Status check result:', { 
          isAuth, 
          isAuthPage, 
          currentPath,
          loading,
          hasUser: !!user,
          hasToken: !!(localStorage.getItem('token') || sessionStorage.getItem('token')),
          from: location.state?.from
        });
        
        // Only redirect if we're not already on the target page to prevent loops
        if (isAuth && isAuthPage) {
          // Special case: if a logged-in student still needs to complete their profile,
          // allow them to stay on the public home page '/' without being bounced.
          const isHome = currentPath === '/';
          const isStudentUser =
            user &&
            (user.isStudent ||
              user.is_student ||
              (user.role_name && user.role_name.toString().toLowerCase() === 'student'));
          const needsCompleteProfile =
            !!(user?.requires_profile_completion || (isStudentUser && user?.requires_grade_selection));

          if (isHome && needsCompleteProfile) {
            console.log('[Auth] Student with incomplete profile on home page; no redirect.');
            // Do not redirect away from home; just let them stay.
          } else {
            // If user is authenticated and tries to access auth pages, redirect to dashboard (or last path)
            const from = location.state?.from?.pathname || '/dashboard';
            console.log('[Auth] Authenticated user on auth page, redirecting to:', from);
            
            // Only navigate if we're not already on the target path and not already redirecting
            if (from !== currentPath && !isRedirecting.current) {
              isRedirecting.current = true;
              navigate(from, { 
                replace: true,
                state: { 
                  ...(location.state || {}), 
                  from: currentPath,
                  timestamp: Date.now()
                }
              });
            }
          }
        } else if (!isAuth && !isPublicRoute) {
          // If user is not authenticated and tries to access protected pages, redirect to login
          console.log('[Auth] Not authenticated, redirecting to login');
          
          // Only navigate if we're not already on the login page and not already redirecting
          if (currentPath !== '/login' && !isRedirecting.current) {
            isRedirecting.current = true;
            navigate('/login', { 
              replace: true,
              state: { 
                ...(location.state || {}),
                from: currentPath,
                message: 'Please log in to continue',
                timestamp: Date.now()
              }
            });
          }
        } else {
          console.log('[Auth] No redirection needed', { isAuth, isAuthPage, currentPath });
        }
      } catch (error) {
        console.error('[Auth] Error during authentication check:', error);
      } finally {
        if (isMounted) {
          authCheckInProgress.current = false;
          // Reset the redirecting flag after a short delay
          setTimeout(() => {
            if (isMounted) {
              isRedirecting.current = false;
            }
          }, 100);
        }
      }
    };
    
    checkAuthAndRedirect();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [loading, isAuthenticated, location, navigate, user]);

  // Global error handler
  React.useEffect(() => {
    const handleError = (event) => {
      console.error('Global error:', event.error);
      setError(event.error?.message || 'An unexpected error occurred');
      setOpenSnackbar(true);
    };

    window.addEventListener('error', handleError);
    return () => {
      window.removeEventListener('error', handleError);
    };
  }, []);

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
    setError(null);
  };

  // Global session conflict handlers
  const handleSessionConflict = (data) => {
    setSessionDialogData(data);
    setShowSessionDialog(true);
  };

  // Expose global handler to window object
  React.useEffect(() => {
    window.handleGlobalSessionConflict = handleSessionConflict;
    return () => {
      delete window.handleGlobalSessionConflict;
    };
  }, []);

  const handleClearOtherSessions = async () => {
    try {
      const email = sessionDialogData?.email;
      const clearToken = sessionDialogData?.clearToken;
      if (!email) {
        console.error('[App] No email found in session dialog data');
        return;
      }
      if (!clearToken) {
        setError('Cannot clear sessions: missing authorization. Please try logging in again.');
        setOpenSnackbar(true);
        return;
      }
      const response = await fetch(`${BASE_URL}/api/accounts/clear-user-sessions/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, clear_token: clearToken })
      });
      
      if (response.ok) {
        const result = await response.json();
        
        // Close the dialog
        setShowSessionDialog(false);
        setSessionDialogData(null);
        
        // Show success message
        console.log('[App] Successfully cleared other sessions:', result.message);
        
        // Trigger login retry by calling the global handler
        if (window.triggerLoginRetry) {
          window.triggerLoginRetry();
        } else {
          // Fallback: redirect to login page
          window.location.href = '/login';
        }
      } else {
        console.error('[App] Failed to clear sessions:', response.status);
        // Show error message to user
        setError('Failed to clear other sessions. Please try again.');
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error('[App] Error clearing sessions:', error);
      // Show error message to user
      setError('Failed to clear other sessions. Please try again.');
      setOpenSnackbar(true);
    }
  };

  const handleCancelSessionConflict = () => {
    setShowSessionDialog(false);
    setSessionDialogData(null);
  };

  // Show loading indicator only during initial auth check
  if (loading && !user) {
    console.log('Showing loading state during initial auth check');
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: 'background.default',
        gap: 2,
        p: 3
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="body1" color="text.secondary">
          Loading your account...
        </Typography>
      </Box>
    );
  }

  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <PWAInstallPrompt />
        <ErrorBoundary>
          <AuthProvider>
            <NudgeProvider>
              <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/get-started" element={<Pricing />} />
              <Route path="/invite/:token" element={<InviteLanding />} />
              <Route path="/join/:token" element={<InviteLanding />} />
              <Route path="/invite-register" element={<InviteRegister onError={setError} />} />
              <Route path="/invalid-invite" element={<div>Invalid or expired invite token. Please contact the person who sent you this invitation.</div>} />
              <Route 
                path="/login" 
                element={
                  user ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <Login />
                  )
                } 
              />
              <Route 
                path="/register" 
                element={
                  user ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <Register onError={setError} />
                  )
                } 
              />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/unauthorized" element={<div>Unauthorized - You don't have permission to access this page</div>} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/terms" element={<LegalContentPage />} />
              <Route path="/privacy" element={<LegalContentPage />} />
              <Route path="/app-link/accept-invite" element={<MobileInviteLinkPage />} />
              <Route path="/accept-invite" element={<AcceptInvitePage />} />
              <Route path="/verify-parent-consent" element={<VerifyParentConsent />} />
              <Route path="/support" element={<SupportRequestPage />} />
              <Route path="/payment" element={<PaymentPage />} />
              <Route path="/payment-failed" element={<PaymentFailedPage />} />
              <Route path="/addon-payment" element={<AddOnPaymentPage />} />
              <Route path="/auth/google/callback" element={<GoogleCalendarCallback />} />
              {/* Protected routes */}

                  <Route 
                    path="/test-results/:attemptId" 
                    element={
                      <ProtectedRoute roles={['student']}>
                        <TestResultsPage />
                      </ProtectedRoute>
                    } 
                  />
                   
             
              
            <Route 
              path="/settings/change-password" 
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/complete-profile" 
              element={
                <ProtectedRoute>
                  {user?.isStudent || user?.is_student || (user?.role_name && user.role_name.toString().toLowerCase() === 'student') ? (
                    <StudentCompleteProfile />
                  ) : (
                    <TeacherCompleteProfile />
                  )}
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/student/complete-profile" 
              element={
                <ProtectedRoute>
                  <StudentCompleteProfile />
                </ProtectedRoute>
              } 
            />

            {/* Dashboard route */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  {!user ? (
                    <div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'100vh'}}>
                      <CircularProgress />
                    </div>
                  ) : user.role_name?.toUpperCase() === 'ADMIN' ? (
                    <AdminDashboard />
                  ) : user.role_name?.toUpperCase() === 'TEACHER' ? (
                    <TeacherLayout>
                      <TeacherDashboard />
                    </TeacherLayout>
                  ) : user.role_name?.toUpperCase() === 'PARENT' ? (
                    <ParentDashboard />
                  ) : user.role_name?.toUpperCase() === 'CHAMPIONS' ? (
                    <ChampionsDashboard />
                  ) : user.role_name?.toUpperCase() === 'STUDENT' ? (
                    <Dashboard />
                  ) : (
                    <Dashboard />
                  )}
                </ProtectedRoute>
              } 
            />

            {/* Profile page */}
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/admin/support-requests" 
              element={
                <ProtectedRoute>
                  <SupportRequestsPage />
                </ProtectedRoute>
              } 
            />

              {/* Admin-only routes */}
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute roles={['ADMIN']}>
                  <ManageUsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/statuses"
              element={
                <ProtectedRoute roles={['ADMIN']}>
                  <StatusManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/features"
              element={
                <ProtectedRoute roles={['ADMIN']}>
                  <FeatureManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pricing-plans"
              element={
                <ProtectedRoute roles={['ADMIN']}>
                  <PricingPlanManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/feature-access"
              element={
                <ProtectedRoute roles={['ADMIN']}>
                  <FeatureAccessManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/user-subscriptions"
              element={
                <ProtectedRoute roles={['ADMIN']}>
                  <UserSubscriptionManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/addon-catalog"
              element={
                <ProtectedRoute roles={['ADMIN']}>
                  <AddOnCatalogManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/tenants"
              element={
                <ProtectedRoute roles={['ADMIN']}>
                  <TenantManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/subjects"
              element={
                <ProtectedRoute roles={['ADMIN']}>
                  <SubjectsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/subject-topics"
              element={
                <ProtectedRoute roles={['ADMIN']}>
                  <SubjectTopicPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/semester-templates"
              element={
                <ProtectedRoute roles={['ADMIN']}>
                  <SemesterTemplatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/boards"
              element={
                <ProtectedRoute roles={['ADMIN']}>
                  <BoardManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/academic-years"
              element={
                <ProtectedRoute roles={['ADMIN']}>
                  <AcademicYearManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/difficulties"
              element={
                <ProtectedRoute roles={['ADMIN']}>
                  <DifficultyManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/meta-tags"
              element={
                <ProtectedRoute roles={['ADMIN']}>
                  <MetaTagManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/exam-modes"
              element={
                <ProtectedRoute roles={['ADMIN']}>
                  <ExamModeManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/referrals"
              element={
                <ProtectedRoute roles={['ADMIN']}>
                  <AdminReferralPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/throttle-config"
              element={
                <ProtectedRoute roles={['ADMIN']}>
                  <ThrottleConfigPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/ai-prompts"
              element={
                <ProtectedRoute roles={['ADMIN']}>
                  <AIPromptsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/questions"
              element={
                <ProtectedRoute roles={['ADMIN']}>
                  <AdminQuestionBankPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/question-analytics"
              element={
                <ProtectedRoute roles={['ADMIN']}>
                  <AdminQuestionAnalyticsPage />
                </ProtectedRoute>
              }
            />

            {/* Teacher-only routes */}
            <Route
              path="/teacher"
              element={
                <ProtectedRoute roles={['teacher']}>
                  <Navigate to="/dashboard" replace />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/teacher/students" 
              element={
                <ProtectedRoute roles={['teacher']}>
                  <TeacherLayout>
                    <StudentsPage />
                  </TeacherLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/teacher/tests" 
              element={
                <ProtectedRoute roles={['teacher']}>
                  <TeacherLayout>
                    <TestsPage />
                  </TeacherLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/teacher/questionbank" 
              element={
                        <ProtectedRoute roles={['teacher']}>
          <TeacherLayout>
            <QuestionBank role="teacher" />
          </TeacherLayout>
        </ProtectedRoute>
              } 
            />
            <Route 
              path="/teacher/assignments" 
              element={
                <ProtectedRoute roles={['teacher']}>
                  <TeacherLayout>
                    <StudentAssignmentsPage />
                  </TeacherLayout>
                </ProtectedRoute>
              } 
            />
            <Route
              path="/teacher/batches"
              element={
                <ProtectedRoute roles={['teacher']}>
                  <TeacherLayout>
                    <TeacherBatchesPage />
                  </TeacherLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/batches/:batchId"
              element={
                <ProtectedRoute roles={['teacher']}>
                  <TeacherLayout>
                    <TeacherBatchDetailPage />
                  </TeacherLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/exam-prep"
              element={
                <ProtectedRoute roles={['teacher']}>
                  <TeacherLayout>
                    <ExamPrepPlannerPage teacherMode />
                  </TeacherLayout>
                </ProtectedRoute>
              }
            />
            <Route 
              path="/teacher/awards" 
              element={
                <ProtectedRoute roles={['teacher']}>
                  <TeacherLayout>
                    <AwardsSetup />
                  </TeacherLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/teacher/tests" 
              element={
                <ProtectedRoute roles={['teacher']}>
                  <TeacherLayout>
                    <TestsPage />
                  </TeacherLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/teacher/calendar" 
              element={
                <ProtectedRoute roles={['teacher']}>
                  <TeacherLayout>
                    <Calendar />
                  </TeacherLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/teacher/analytics" 
              element={
                <ProtectedRoute roles={['teacher']}>
                  <TeacherLayout>
                    <TeacherAnalyticsWrapper />
                  </TeacherLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/analytics/teacher-wrapper" 
              element={
                <ProtectedRoute roles={['teacher']}>
                  <TeacherLayout>
                    <TeacherAnalyticsWrapper />
                  </TeacherLayout>
                </ProtectedRoute>
              } 
            />

            {/* Champions-only routes */}
            <Route 
              path="/champions/dashboard" 
              element={
                <ProtectedRoute roles={['champions']}>
                  <ChampionsDashboard />
                </ProtectedRoute>
              } 
            />
                         <Route 
               path="/champions/referral" 
               element={
                 <ProtectedRoute roles={['champions']}>
                   <ChampionsReferralWrapper />
                 </ProtectedRoute>
               } 
             />
                         <Route 
               path="/champions/questionbank" 
               element={
                 <ProtectedRoute roles={['champions']}>
                   <ChampionsQuestionBankWrapper />
                 </ProtectedRoute>
               } 
             />

            {/* Parent-only routes */}
            <Route
              path="/parent/children"
              element={
                <ProtectedRoute roles={['parent']}>
                  <ParentChildrenPage />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/parent/awards" 
              element={
                <ProtectedRoute roles={['parent']}>
                  <AwardsSetup />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/parent/assignments" 
              element={
                <ProtectedRoute roles={['parent']}>
                  <ParentLayout>
                    <StudentAssignmentsPage />
                  </ParentLayout>
                </ProtectedRoute>
              } 
            />
            <Route
              path="/parent/progress"
              element={
                <ProtectedRoute roles={['parent']}>
                  <StudentAnalyticsDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/exam-prep"
              element={
                <ProtectedRoute roles={['parent']}>
                  <ParentLayout>
                    <ExamPrepPlannerPage parentMode />
                  </ParentLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/test/:testId/instructions"
              element={
                <ProtectedRoute roles={['student', 'teacher', 'parent']}>
                  <TestFlowShell>
                    <TestInstructions />
                  </TestFlowShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/test/:testId"
              element={
                <ProtectedRoute roles={['student', 'teacher', 'parent']}>
                  <TestFlowShell>
                    <TestTaking />
                  </TestFlowShell>
                </ProtectedRoute>
              }
            />
            {/* Profile Builder */}
            <Route 
              path="/profile-builder" 
              element={
                <ProtectedRoute roles={['student']}>
                  <ProfileBuilder />
                </ProtectedRoute>
              } 
            />

            {/* Student-only routes */}
            {!user?.isTeacher && (
              <>
                <Route 
                  path="/tests" 
                  element={
                    <ProtectedRoute roles={['student']}>
                      <Tests />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/results" 
                  element={
                    <ProtectedRoute roles={['student']}>
                      <ResultsPage />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/revision-notes"
                  element={
                    <ProtectedRoute roles={['student']}>
                      <RevisionNotesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics/student"
                  element={
                    <ProtectedRoute roles={['student']}>
                      <StudentAnalyticsDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/student/exam-prep"
                  element={
                    <ProtectedRoute roles={['student']}>
                      <ExamPrepPlannerPage />
                    </ProtectedRoute>
                  }
                />
                <Route 
                  path="/tests/:attemptId/review" 
                  element={
                    <ProtectedRoute roles={['student']}>
                      <ReviewAnswers />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/test-results/:attemptId" 
                  element={
                    <ProtectedRoute roles={['student']}>
                      <TestResultsPage />
                    </ProtectedRoute>
                  } 
                />
              </>
            )}
            
            {/* Analytics routes */}
            <Route
              path="/analytics"
              element={
                <ProtectedRoute roles={['student', 'teacher']}>
                  <AnalyticsIndex />
                </ProtectedRoute>
              }
            />
            
            {/* Catch-all route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          
          {/* Global error snackbar */}
          <Snackbar 
            open={openSnackbar}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert 
              onClose={handleCloseSnackbar} 
              severity={error?.includes('success') ? 'success' : 'error'} 
              sx={{ width: '100%' }}
            >
              {error}
            </Alert>
          </Snackbar>

          {/* Global Session Conflict Dialog */}
          <Dialog open={showSessionDialog} onClose={handleCancelSessionConflict} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>
              <Typography variant="h6" component="div">
                Active Session Detected
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
                  {sessionDialogData?.message || 'You are already logged in on another device.'}
                </Typography>
                {sessionDialogData?.existingSessionIp && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    IP Address: {sessionDialogData.existingSessionIp}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  You can either:
                </Typography>
                <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Clear the other session and continue here
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Cancel and use the other session
                  </Typography>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button 
                onClick={handleCancelSessionConflict} 
                variant="outlined"
                sx={{ minWidth: 100 }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleClearOtherSessions} 
                variant="contained" 
                color="primary"
                sx={{ minWidth: 120 }}
              >
                Clear Other Session
              </Button>
            </DialogActions>
          </Dialog>
            </NudgeProvider>
          </AuthProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
